import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, User, X } from 'lucide-react'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import {
  fetchReviewDetail,
  type QuebonReviewDetail,
} from '@/api/quebonReviews'

interface Props {
  reviewId: number | null
  open: boolean
  onClose: () => void
}

export default function ReviewDetailDialog({
  reviewId,
  open,
  onClose,
}: Props) {
  const { t } = useTranslation('common')
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<QuebonReviewDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useFocusTrap(dialogRef, open && !!data)

  useEffect(() => {
    if (!open || reviewId == null) {
      setData(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchReviewDetail(reviewId)
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : t('reviews.dialog.loadFailed'),
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, reviewId, t])

  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-detail-title"
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-2xl shadow-[0_8px_16px_rgba(0,0,0,0.2)] w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2
              id="review-detail-title"
              className="text-[22px] font-bold text-[#131313] leading-snug flex-1 min-w-0"
            >
              {data?.title ?? (loading ? t('reviews.dialog.loading') : '')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="close"
              className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center hover:bg-gray-100 text-gray-600"
            >
              <X className="size-5" />
            </button>
          </div>

          {data && (
            <div className="flex items-center gap-4 text-[13px] text-gray-600 mb-5">
              <span className="inline-flex items-center gap-1">
                <User className="size-4" aria-hidden />
                {data.writer?.name ?? ''}
              </span>
              <span className="inline-flex items-center gap-1">
                <Eye className="size-4" aria-hidden />
                {data.views}
              </span>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            {loading && (
              <p className="text-center text-gray-500 py-8">
                {t('reviews.dialog.loading')}
              </p>
            )}
            {error && (
              <p className="text-center text-red-600 py-8">{error}</p>
            )}
            {data && (
              <div
                className="text-[15px] text-[#333] leading-[1.7]"
                dangerouslySetInnerHTML={{
                  __html: data.text.replace(/\n/g, '<br/>'),
                }}
              />
            )}
          </div>

          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 h-11 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
            >
              {t('reviews.dialog.viewList')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
